import { FC, MouseEvent } from 'react'
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid'
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PowerIcon,
} from '@heroicons/react/24/outline'
import { formatOpsDateTime, formatOpsDateTimeShort, formatRelativeSince } from '@/services/operations'
import pluralize from 'pluralize'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { CgSpinner } from 'react-icons/cg'

import { OpsGroupsListItem } from '@/models/operations/types'
import { ButtonApp } from '@/components'
import { classNames } from '@/utils'

import { GroupBulkActionButtons } from './GroupBulkActionButtons'
import { OPS_BULK_REFRESH_LABEL, OPS_GROUP_CHECK_STATUS_TOOLTIP, OPS_GROUP_VIEW_DETAILS_TOOLTIP } from './opsUiShared'

type OpsGroupCardProps = {
  group: OpsGroupsListItem
  onNavigate: (groupId: string) => void
  onRefreshGroup: (groupId: string) => void
  onRedeployAll: (group: OpsGroupsListItem) => void
  onClearCacheAll: (group: OpsGroupsListItem) => void
  isBulkRunning?: boolean
  isGroupRefreshing?: boolean
  isGlobalRefresh?: boolean
}

const StatusPill: FC<{
  label: string
  count: number
  color: 'green' | 'blue' | 'red' | 'amber'
}> = ({ label, count, color }) => (
  <span
    className={classNames(
      'py-1 px-3 border rounded-2xl text-xs flex items-center gap-2 text-gray-400',
      { 'border-[#99E0BA]': color === 'green' },
      { 'border-blue-200': color === 'blue' },
      { 'border-red-200': color === 'red' },
      { 'border-amber-200': color === 'amber' },
    )}
  >
    <span>{label}</span>
    <span
      className={classNames('text-white rounded-3xl py-1 px-2 min-w-[1.5rem] text-center text-[11px] font-semibold', {
        'bg-[#288F58]': color === 'green',
        'bg-[#2E59A3]': color === 'blue',
        'bg-red-200': color === 'red',
        'bg-yellow-200': color === 'amber',
      })}
    >
      {count}
    </span>
  </span>
)


const OpsGroupCard: FC<OpsGroupCardProps> = ({
  group,
  onNavigate,
  onRefreshGroup,
  onRedeployAll,
  onClearCacheAll,
  isBulkRunning,
  isGroupRefreshing = false,
  isGlobalRefresh = false,
}) => {
  const { summary, last_refreshed_at, health, loadError } = group
  const hasLoadError = Boolean(loadError)
  const refreshDisabled = isGroupRefreshing || isGlobalRefresh
  const cardBulkDisabled = isBulkRunning || isGroupRefreshing || isGlobalRefresh

  const handleRefresh = (e: MouseEvent) => {
    e.stopPropagation()
    onRefreshGroup(group.id)
  }

  const menuItems = [
    ...(hasLoadError
      ? []
      : [
          {
            label: 'View Details',
            icon: <EyeIcon className="w-4" />,
            command: () => onNavigate(group.id),
          },
        ]),
    {
      label: OPS_BULK_REFRESH_LABEL,
      icon: <ArrowPathIcon className="w-4" />,
      command: () => onRefreshGroup(group.id),
    },
  ]

  return (
    <div
      className={classNames(
        'h-full bg-white p-3 border border-gray-100 border-l-4 flex flex-col gap-3 rounded-[4px] select-none',
        { 'cursor-pointer': !hasLoadError },
        { 'cursor-default': hasLoadError },
        { 'border-l-green-200': health === 'HEALTHY' && !hasLoadError },
        { 'border-l-blue-200': health === 'DEGRADED' && !hasLoadError },
        { 'border-l-red-200': health === 'FAILED' || hasLoadError },
      )}
      role={hasLoadError ? undefined : 'button'}
      tabIndex={hasLoadError ? undefined : 0}
      onDoubleClick={hasLoadError ? undefined : () => onNavigate(group.id)}
      onKeyDown={
        hasLoadError
          ? undefined
          : (e) => {
              if (e.key === 'Enter') onNavigate(group.id)
            }
      }
    >
      <div className="flex justify-between">
        <span className="font-medium text-gray-700 truncate flex-1 min-w-0 pr-3" title={group.name}>
          {group.name}
        </span>
        <div className="flex gap-2 items-center shrink-0 ml-auto">
          {hasLoadError && (
            <span className="text-xs font-medium text-red-200 bg-red-50 border border-red-200 rounded px-2 py-0.5">
              LOAD FAILED
            </span>
          )}
          <Menu>
              <MenuButton onClick={(e) => e.stopPropagation()}>
                <EllipsisVerticalIcon width={22} className="text-gray-600" />
              </MenuButton>
              <MenuItems
                anchor="left start"
                className="bg-white p-2 shadow-xl flex flex-col gap-2 z-10"
              >
                {menuItems.map(({ icon, label, command }) => (
                  <MenuItem key={label}>
                    <ButtonApp
                      className="px-4 py-1.5 gap-2"
                      isPrimary
                      handleClick={(e) => {
                        e.stopPropagation()
                        command()
                      }}
                      isDisabled={label === OPS_BULK_REFRESH_LABEL && refreshDisabled}
                    >
                      {icon}
                      <span className="text-sm">{label}</span>
                    </ButtonApp>
                  </MenuItem>
                ))}
              </MenuItems>
            </Menu>
        </div>
      </div>

      {hasLoadError ? (
        <div className="flex items-start gap-2 rounded bg-red-50 border border-red-200 p-2.5 text-xs text-red-200">
          <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-medium">Could not load stores for this group</p>
            <p className="mt-1 break-words">{loadError}</p>
            <button
              type="button"
              className="mt-2 font-medium underline hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={refreshDisabled}
              onClick={handleRefresh}
            >
              {isGroupRefreshing ? 'Retrying…' : 'Retry'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-6">
            <div className="inline-flex items-center gap-2 text-gray-500 font-medium">
              <BuildingStorefrontIcon className="h-5 w-5 shrink-0" />
              <span className="text-xs leading-5">
                {summary.total} {pluralize('STORE', summary.total).toUpperCase()}
              </span>
            </div>
            <div className="inline-flex items-center gap-2 text-gray-500 font-medium">
              <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-500" />
              <span className="text-xs leading-5">{summary.active} ACTIVE</span>
            </div>
            <div className="inline-flex items-center gap-2 text-gray-500 font-medium">
              <PowerIcon className="h-5 w-5 shrink-0 text-red" />
              <span className="text-xs leading-5">{summary.churned} CHURNED</span>
            </div>
          </div>

          {(summary.running > 0 || summary.building > 0 || summary.failed > 0) && (
            <div className="flex flex-col gap-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                Deploy status
              </p>
              <div className="flex gap-2 flex-wrap">
                {summary.running > 0 && (
                  <StatusPill label="Ready" count={summary.running} color="green" />
                )}
                {summary.failed > 0 && (
                  <StatusPill label="Failed" count={summary.failed} color="red" />
                )}
                {summary.building > 0 && (
                  <StatusPill label="Build" count={summary.building} color="blue" />
                )}
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-gray-400 text-xs min-h-12 line-clamp-3">{group.description || '\u00A0'}</p>

      {!hasLoadError && (
        <GroupBulkActionButtons
          stopPropagation
          disabled={cardBulkDisabled || summary.active === 0}
          refreshDisabled={refreshDisabled}
          onRedeploy={() => onRedeployAll(group)}
          onClearCache={() => onClearCacheAll(group)}
        />
      )}

      <div className="flex items-center justify-between mt-auto gap-3">
        <p
          className="text-xs min-w-0 truncate leading-none text-gray-500"
          title={last_refreshed_at ? formatOpsDateTime(last_refreshed_at) : undefined}
        >
          <span className="font-medium">Last refreshed</span>
          {last_refreshed_at ? (
            <>
              <span className="text-gray-300"> · </span>
              <span className="font-medium text-gray-800">{formatRelativeSince(last_refreshed_at)}</span>
              <span className="font-normal text-gray-400">
                {' '}
                · {formatOpsDateTimeShort(last_refreshed_at)}
              </span>
            </>
          ) : (
            <span className="text-gray-400"> —</span>
          )}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            className="text-gray-400 hover:text-green-600 p-1 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={refreshDisabled}
            title={OPS_GROUP_CHECK_STATUS_TOOLTIP}
            aria-label={OPS_GROUP_CHECK_STATUS_TOOLTIP}
            onClick={handleRefresh}
          >
            {isGroupRefreshing ? (
              <CgSpinner className="w-5 h-5 animate-spin" />
            ) : (
              <ArrowPathIcon className="w-5 h-5" />
            )}
          </button>
          {!hasLoadError && (
            <button
              type="button"
              className="text-gray-400 hover:text-green-600 p-1"
              title={OPS_GROUP_VIEW_DETAILS_TOOLTIP}
              aria-label={OPS_GROUP_VIEW_DETAILS_TOOLTIP}
              onClick={(e) => {
                e.stopPropagation()
                onNavigate(group.id)
              }}
            >
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export { OpsGroupCard }
